import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslocoService } from "@ngneat/transloco";
import { FormControl } from "@angular/forms";
import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { ToastrService } from "ngx-toastr";
import { debounceTime, distinctUntilChanged, filter, take } from "rxjs/operators";
import { debounce } from "utils-decorators";
import { Compbaser } from "../../../../components/compbaser";
import { IPlayerProps } from "../../../../interfaces";
import { actionUpdateSceneItem, actionSelectedSceneTouchId } from "../../../../store/actions/user.actions";
import { AppService, SceneAddService, StoreSelectorsService } from "../../../../services";
import { ToggleOption } from "../../../../components/setting-toggle-group/setting-toggle-group.component";
import { DialogTouchActionComponent } from "../touch-action-dialog/touch-action-dialog.component";
import { DialogPopComponent } from "../../../../components/dialog-pop/dialog-pop.component";
import { DialogSwitchConfirmComponent, SwitchConfirmDialogData } from "../dialog-switch-confirm/dialog-switch-confirm.component";
import { Slide, TouchLib } from "../touchLib";
import * as _ from "lodash";


@Component({
    selector: "app-slideer-navigation",
    templateUrl: "./touch-navigation.component.html",
    styleUrls: ["./touch-navigation.component.scss"],
})
export class TouchNavigationComponent extends Compbaser implements OnInit {
    sliderScene: IPlayerProps;
    sliderSceneId;
    selectedSceneItemId: string;
    durationControl = new FormControl();
    navigationMode: "slide" | "time" = "slide";
    slideList: Slide[];
    editingSlideSceneId: string;
    selectedSlide: any = null;
    selectedSlideScene: IPlayerProps = null;

    // Track the slide that was selected when drag started
    private dragStartSlideId: string = null;

    // Navigation button customization
    navigationButtonSize = null;

    // Navigation options for toggle component
    navigationOptions: ToggleOption[] = [];

    // Size options for UI
    sizeOptions = [];

    @Input()
    set setSliderScene(i_sliderScene) {
        if (!i_sliderScene) return;
        this.sliderScene = i_sliderScene;
        this.configureProps();
    }

    @Output() remove = new EventEmitter<void>();
    @Output() update = new EventEmitter<any>();
    @Output() selectFirstSlide = new EventEmitter<{ index: number; slideSceneId: string }>();
    
    constructor(
        private storeSelector: StoreSelectorsService,
        private translocoService: TranslocoService,
        private toastr: ToastrService,
        private sceneAddService: SceneAddService,
        private appService: AppService,
        public override dialog: MatDialog,
        private cdr: ChangeDetectorRef,
    ) {
        super();
        this.me = 'touch-navigation';
        // Initialize navigation options
        this.navigationOptions = [
            { value: "slide", label: "slide" },
            { value: "time", label: "time" },
        ];
    }

    ngOnInit() {
        this.sceneAddService.setBlockType("BLOCK_COMPONENTS");

        // Initialize size options with translations
        this.sizeOptions = [
            { value: 1, label: `x1 (${this.translocoService.translate('ai.tiny', {}, this.translocoService.getActiveLang())})` },
            { value: 2, label: `x2 (${this.translocoService.translate('ai.small', {}, this.translocoService.getActiveLang())})` },
            { value: 3, label: `x3 (${this.translocoService.translate('ai.medium', {}, this.translocoService.getActiveLang())})` },
            { value: 4, label: `x4 (${this.translocoService.translate('ai.large', {}, this.translocoService.getActiveLang())})` },
            { value: 5, label: `x5 (${this.translocoService.translate('ai.xlarge', {}, this.translocoService.getActiveLang())})` },
        ];

        // Setup reactive duration control
        this.cod(
            this.durationControl.valueChanges
                .pipe(
                    debounceTime(500),
                    distinctUntilChanged(),
                    filter((value) => value !== null && value !== undefined && !isNaN(value)),
                )
                .subscribe((duration) => {
                    this.saveDuration(duration);
                }),
        );

        this.cod(
            this.storeSelector.getSliderSelection().subscribe((sliderSelection) => {
                // Only process if this matches the current slider being edited
                this.editingSlideSceneId = null;
                this.selectedSlide = null;
                if (sliderSelection.sceneId === this.sliderSceneId) {
                    this.editingSlideSceneId = sliderSelection.ScenePlaylistId;
                    this.updateSelectedSlide();
                }
            }),
        );

        this.cod(
            this.storeSelector.listenSelectedSceneItemId()
                .subscribe((value) => {
                    this.selectedSceneItemId = value;
                }),
        );
    }

    private configureProps() {
        this.sliderSceneId = null;
        if (this.sliderScene) {
            this.sliderSceneId = this.sliderScene?.Player?.$?.id;
            this.navigationMode = TouchLib.isCollection(this.sliderScene) ? "slide" : "time";
            this.durationControl.setValue(this.getDuration(this.sliderScene), { emitEvent: false });
            // this.slideList = TouchLib.extractSlidesFromSlider(this.sliderScene);
            // this.updateSelectedSlide();
        }
    }

    private updateSelectedSlide() {
        if (this.editingSlideSceneId && this.slideList) {
            this.selectedSlide = this.slideList.find(slide => slide.sceneId === this.editingSlideSceneId);
            // TEMPORARILY DISABLED - The scene data subscription seems to trigger the issue
            // Get the actual scene data for the selected slide with defensive programming
            // if (this.selectedSlide) {
            //     this.cod(
            //         this.storeSelector.listenSceneData(this.editingSlideSceneId)
            //             .pipe(
            //                 take(1), // Only take one value to prevent memory leaks
            //                 distinctUntilChanged() // Prevent unnecessary updates
            //             )
            //             .subscribe((sceneData: IPlayerProps) => {
            //                 if (sceneData) {
            //                     this.selectedSlideScene = sceneData;
            //                 }
            //             })
            //     );
            // }
        } else {
            this.selectedSlide = null;
            this.selectedSlideScene = null;
        }
    }

    private saveDuration(duration: number) {
        if (!this.sliderScene) return;

        // Duration is only for Playlists
        if (TouchLib.getComponentName(this.sliderScene) !== 'Playlist') return;

        const playlistId = TouchLib.getPlaylistId(this.sliderScene);
        if (!playlistId) return;

        const validDuration = Math.max(1, duration || 5); // Ensure minimum 1 second

        this.storeSelector.dispatch(
            actionUpdateSceneItem({
                sceneId: this.sliderSceneId,
                sceneItemId: playlistId,
                values: [
                    {
                        key: "duration",
                        value: validDuration.toString(),
                        storePath: "Data[0].Playlist[0].$",
                    },
                ],
                disableCRUD: true,
            }),
        );
        setTimeout(() => {
            this.appService.updateMultiScene([this.sliderSceneId]);
        });
    }

    onChangeNavigationMode(selectedValue?: string) {
        if (selectedValue) {
            this.navigationMode = selectedValue as "slide" | "time";
        }

        const prevComponentName = this.navigationMode === "slide" ? "Playlist" : "Collection";
        const newComponentName = this.navigationMode === "slide" ? "Collection" : "Playlist";

        const fromLabel = this.translocoService.translate(`ai.${prevComponentName.toLowerCase()}`, {}, this.translocoService.getActiveLang());
        const toLabel = this.translocoService.translate(`ai.${newComponentName.toLowerCase()}`, {}, this.translocoService.getActiveLang());

        const dialogData: SwitchConfirmDialogData = {
            fromComponent: prevComponentName as 'Collection' | 'Playlist',
            toComponent: newComponentName as 'Collection' | 'Playlist'
        };

        const dialogRef = this.dialog.open(DialogSwitchConfirmComponent, {
            width: '500px',
            data: dialogData,
            disableClose: true
        });

        this.cod(
            dialogRef.afterClosed().subscribe(result => {
            if (!result) {
                this.navigationMode = this.navigationMode === "slide" ? "time" : "slide";
                this.cdr.detectChanges(); // Force change detection to revert UI state
                return;
            }

            const players = this.sliderScene.Player.Data[0].Scene[0].Players[0].Player;
            const prevBlock = players.find(p => p.Data?.[0]?.[prevComponentName]);

            if (!prevBlock) {
                this.toastr.error(this.translocoService.translate('ai.couldNotFindCollectionPlaylist', {}, this.translocoService.getActiveLang()));
                return;
            }

            const prevPages = prevBlock.Data[0][prevComponentName][0].Page;
            const prevLayout = prevBlock.Data[0].Layout;

            let blockData;

            if (this.navigationMode === "time") {
                blockData = _.cloneDeep(this.storeSelector.blockLabels.PLAYLIST.template);
            } else {
                blockData = _.cloneDeep(this.storeSelector.blockLabels.COLLECTION.template);
            }

            blockData.Data[0].Layout = prevLayout;
            blockData.$.id = prevBlock.$.id;
            blockData.Data[0][newComponentName][0].Page = prevPages;

            const prevBlockIndex = players.findIndex(p => p.$.id === prevBlock.$.id);
            const finalPlayersArray = [...players];
            finalPlayersArray[prevBlockIndex] = blockData;

            this.storeSelector.dispatch(
                actionUpdateSceneItem({
                    sceneId: this.sliderSceneId,
                    sceneItemId: this.sliderSceneId,
                    values: [
                        {
                            key: "Player",
                            value: finalPlayersArray,
                            storePath: "Data[0].Scene[0].Players[0]",
                        },
                    ],
                    disableCRUD: true,
                }),
            );

            setTimeout(() => {
                this.removeAllNavigation();
                this.appService.updateMultiScene([this.sliderSceneId]);

                // Emulate clicking the first slide in the left sidebar
                setTimeout(() => {
                    if (prevPages && prevPages.length > 0) {
                        const firstSlideId = prevPages[0]?.Player?.[0]?.$?.src;
                        if (firstSlideId) {
                            this.selectFirstSlide.emit({ index: 0, slideSceneId: firstSlideId });
                        }
                    }
                }, 300);
            });
        }),
        );
    }

    getDuration(row) {
        return parseInt(TouchLib.getDuration(row)) || 5;
    }

    @debounce(333)
    onChangeSceneName(event) {
        const sceneName = event.target.value;
        if (!sceneName || !this.editingSlideSceneId) return;

        // Check if the name actually changed to avoid unnecessary updates
        const currentName = this.selectedSlideScene?.Player?.$.label || this.selectedSlide?.title || "";
        if (sceneName === currentName) return;

        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.editingSlideSceneId,
            sceneItemId: this.editingSlideSceneId,
            values: [{
                key: "label",
                value: sceneName,
                storePath: "$",
            }],
            disableCRUD: true,
        }));

        // Batch scene updates with longer delay to prevent frequent redraws
        setTimeout(() => {
            this.appService.updateMultiScene([this.editingSlideSceneId]);
        }, 500);
    }

    addOrDragActionButton(event: CdkDragDrop<any> = null) {
        if (!this.editingSlideSceneId) {
            this.toastr.warning(this.translocoService.translate("toast.selectSlide", {}, this.translocoService.getActiveLang()));
            return;
        }

        let offset;

        // Handle drag events with slide validation
        if (event) {
            if (event.isPointerOverContainer) return;

            // Get drop coordinates and find target slide
            const dropX = event.dropPoint?.x || 0;
            const dropY = event.dropPoint?.y || 0;
            const slideElements = document.querySelectorAll('.slide-item[data-slide-id]');
            let targetSlideId = null;

            slideElements.forEach((slideEl: Element) => {
                const rect = slideEl.getBoundingClientRect();
                const slideId = slideEl.getAttribute('data-slide-id');

                if (dropX >= rect.left && dropX <= rect.right &&
                    dropY >= rect.top && dropY <= rect.bottom) {
                    targetSlideId = slideId;
                }
            });

            // Validate against originally selected slide
            if (targetSlideId !== this.dragStartSlideId) {
                this.toastr.warning(this.translocoService.translate('ai.canOnlyDropOnSelectedSlide', {}, this.translocoService.getActiveLang()));
                return;
            }

            // Proceed with touch button creation
            offset = this.sceneAddService.getOffset(event);
        } else {
            // Handle non-drag button clicks (Add Touch Button)
            offset = {
                offsetX: 50,
                offsetY: 50,
            };
        }

        this.dialog.open(DialogTouchActionComponent, {
            width: "500px",
            data: {
                sliderScene: this.sliderScene,
                newTouchButton: true,
                offset,
            },
        });
    }

    onDragStart() {
        this.dragStartSlideId = this.editingSlideSceneId;

        document.body.classList.add('touch-button-dragging');
        if (this.editingSlideSceneId) {
            document.body.classList.add('valid-slide-selected');
        } else {
            document.body.classList.add('no-slide-selected');
        }
    }

    onDragEnd() {
        document.body.classList.remove('touch-button-dragging', 'valid-slide-selected', 'no-slide-selected');
    }

    onSizeChange() {
        if (!this.navigationButtonSize) {
            return;
        }
        this.toastr.info(this.translocoService.translate('ai.navigationButtonsWillShow', {}, this.translocoService.getActiveLang()));
        // Auto-execute resetNavigation when size changes
        // Navigation buttons are now applied to the wrapper scene, not individual slides
        this.update.emit({
            size: this.navigationButtonSize
        });
    }

    removeAllNavigation() {
        this.toastr.warning(this.translocoService.translate('ai.navigationButtonsWillRemove', {}, this.translocoService.getActiveLang()));
        this.navigationButtonSize = null;
        this.remove.emit();
    }
}
